import jsQR from 'jsqr';

class QRCodeScanner {
  constructor() {
    this.isScanning = false;
    this.lastDetectedCode = null;
    this.detectionCallback = null;
  }

  /**
   * Scan QR code from video element
   * @param {HTMLVideoElement} videoElement - Video element with camera feed
   * @returns {Object} QR code detection result
   */
  scanFromVideo(videoElement) {
    try {
      if (!videoElement || videoElement?.readyState !== videoElement?.HAVE_ENOUGH_DATA) {
        return { success: false, error: 'Video not ready' };
      }

      // Create canvas to capture video frame
      const canvas = document.createElement('canvas');
      canvas.width = videoElement?.videoWidth;
      canvas.height = videoElement?.videoHeight;
      const ctx = canvas?.getContext('2d');
      
      if (!ctx) {
        return { success: false, error: 'Canvas context unavailable' };
      }

      // Draw current video frame
      ctx?.drawImage(videoElement, 0, 0, canvas?.width, canvas?.height);
      
      // Get image data
      const imageData = ctx?.getImageData(0, 0, canvas?.width, canvas?.height);
      
      // Scan for QR code
      const code = jsQR(imageData?.data, imageData?.width, imageData?.height, {
        inversionAttempts: 'dontInvert'
      });

      if (code) {
        const qrData = this.parseQRData(code?.data);
        this.lastDetectedCode = qrData;
        
        return {
          success: true,
          qrCode: qrData,
          rawData: code?.data,
          location: code?.location
        };
      }

      return { success: false, noCodeDetected: true };
    } catch (error) {
      console.error('QR scan error:', error);
      return { success: false, error: error?.message };
    }
  }

  /**
   * Parse QR code data to extract product information
   * @param {string} rawData - Raw QR code data
   * @returns {Object} Parsed product information
   */
  parseQRData(rawData) {
    try {
      // Try to parse as JSON first (structured product data)
      const jsonData = JSON.parse(rawData);
      return {
        type: 'structured',
        productId: jsonData?.productId || jsonData?.id,
        productName: jsonData?.name || jsonData?.productName,
        price: jsonData?.price,
        category: jsonData?.category,
        barcode: jsonData?.barcode || jsonData?.upc,
        brand: jsonData?.brand,
        description: jsonData?.description,
        rawData: rawData
      };
    } catch (e) {
      // Not JSON, check if it's a URL
      if (rawData?.startsWith('http://') || rawData?.startsWith('https://')) {
        return {
          type: 'url',
          url: rawData,
          productId: this.extractProductIdFromUrl(rawData),
          rawData: rawData
        };
      }

      // Check if it's a barcode/UPC format
      if (/^\d{8,14}$/?.test(rawData)) {
        return {
          type: 'barcode',
          barcode: rawData,
          productId: rawData,
          rawData: rawData
        };
      }

      // Plain text or unknown format
      return {
        type: 'text',
        text: rawData,
        productId: rawData,
        rawData: rawData
      };
    }
  }

  /**
   * Extract product ID from URL
   * @param {string} url - Product URL
   * @returns {string|null} Extracted product ID
   */
  extractProductIdFromUrl(url) {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj?.pathname?.split('/')?.filter(Boolean);
      
      // Common patterns: /product/123, /item/abc, /p/xyz
      const productIndex = pathParts?.findIndex(part => 
        ['product', 'item', 'p', 'products']?.includes(part?.toLowerCase())
      );
      
      if (productIndex !== -1 && pathParts?.[productIndex + 1]) {
        return pathParts?.[productIndex + 1];
      }
      
      // Try query parameters
      const productId = urlObj?.searchParams?.get('id') || 
                       urlObj?.searchParams?.get('productId') ||
                       urlObj?.searchParams?.get('pid');
      
      return productId || pathParts?.[pathParts?.length - 1];
    } catch (error) {
      return null;
    }
  }

  /**
   * Start continuous QR code scanning
   * @param {HTMLVideoElement} videoElement - Video element
   * @param {Function} callback - Callback function when QR detected
   * @param {number} interval - Scan interval in milliseconds
   * @returns {number} Interval ID
   */
  startContinuousScanning(videoElement, callback, interval = 500) {
    this.isScanning = true;
    this.detectionCallback = callback;

    const scanInterval = setInterval(() => {
      if (!this.isScanning) {
        clearInterval(scanInterval);
        return;
      }

      const result = this.scanFromVideo(videoElement);
      
      if (result?.success && result?.qrCode) {
        // Only trigger callback if it's a new code
        if (result?.qrCode?.rawData !== this.lastDetectedCode?.rawData) {
          callback?.(result);
        }
      }
    }, interval);

    return scanInterval;
  }

  /**
   * Stop continuous scanning
   */
  stopScanning() {
    this.isScanning = false;
    this.detectionCallback = null;
  }

  /**
   * Reset last detected code
   */
  reset() {
    this.lastDetectedCode = null;
  }

  /**
   * Mock product database lookup
   * In production, this would call a real product API
   * @param {Object} qrData - Parsed QR data
   * @returns {Object} Product information
   */
  async lookupProduct(qrData) {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 300));

    // Mock product database
    const mockProducts = {
      'PROD-001': {
        productId: 'PROD-001',
        name: 'Wireless Headphones',
        price: 149.99,
        category: 'Electronics',
        brand: 'AudioTech',
        description: 'Premium noise-cancelling wireless headphones'
      },
      'PROD-002': {
        productId: 'PROD-002',
        name: 'Smart Watch',
        price: 299.99,
        category: 'Electronics',
        brand: 'TechWear',
        description: 'Fitness tracking smartwatch with heart rate monitor'
      },
      'PROD-003': {
        productId: 'PROD-003',
        name: 'Coffee Maker',
        price: 89.99,
        category: 'Home',
        brand: 'BrewMaster',
        description: 'Programmable coffee maker with thermal carafe'
      }
    };

    // Try to find product by ID
    if (qrData?.productId && mockProducts?.[qrData?.productId]) {
      return {
        success: true,
        product: mockProducts?.[qrData?.productId],
        source: 'database'
      };
    }

    // If QR data already has product info, use it
    if (qrData?.productName && qrData?.price) {
      return {
        success: true,
        product: {
          productId: qrData?.productId || 'UNKNOWN',
          name: qrData?.productName,
          price: qrData?.price,
          category: qrData?.category || 'Shopping',
          brand: qrData?.brand || 'Unknown',
          description: qrData?.description || ''
        },
        source: 'qr_data'
      };
    }

    // Fallback: generate estimated product info
    return {
      success: true,
      product: {
        productId: qrData?.productId || qrData?.barcode || 'UNKNOWN',
        name: qrData?.text || 'Unknown Product',
        price: 99.99,
        category: 'Shopping',
        brand: 'Unknown',
        description: 'Product information not available'
      },
      source: 'estimated'
    };
  }
}

export default QRCodeScanner;